import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { Avatar, Button, Card, Checkbox, Form, Input, List, Select, Tooltip } from 'antd';
import { comboColorMap, ORIENTATION_OPTIONS, POSTER_LANGUAGE_OPTIONS } from 'constants/constant';
import { ThumbnailInterface } from 'content/type';
import React from 'react';

interface ThumbnailListProps {
  thumbnails: ThumbnailInterface[];
  selectedThumbnail: ThumbnailInterface | null;
  onThumbnailSelect: (thumbnail: ThumbnailInterface | null) => void;
  onThumbnailRemove: (thumbnail: ThumbnailInterface) => void;
  onThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
  onNewThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
  canDownload?: boolean;
  isNew?: boolean;
}

// 언어 값을 정규화하는 함수 (소문자/다양한 형식을 옵션 형식으로 변환)
const normalizeLanguage = (lang: string | undefined): string | undefined => {
  if (!lang) return undefined;

  const normalized = lang.toUpperCase();

  // 특수 케이스 처리
  const mapping: Record<string, string> = {
    KR: 'KO',
    JP: 'JA',
    ZH: 'ZH-HANS',
  };

  // 직접 매칭되는 경우
  if (POSTER_LANGUAGE_OPTIONS.some(opt => opt.value === normalized)) {
    return normalized;
  }

  // 매핑이 필요한 경우
  if (mapping[normalized]) {
    return mapping[normalized];
  }

  // 기본값으로 KO 반환
  return 'KO';
};

const ThumbnailList: React.FC<ThumbnailListProps> = ({
  thumbnails,
  selectedThumbnail,
  onThumbnailSelect,
  onThumbnailRemove,
  onThumbnailChange,
  onNewThumbnailChange,
  canDownload,
  isNew,
}) => {
  const handleThumbnailDownload = async (thumbnail: ThumbnailInterface): Promise<void> => {
    try {
      const fileUrl = thumbnail.file || thumbnail.originFileObj?.name;

      if (!fileUrl) {
        throw new Error('No valid file URL found for download');
      }

      const response = await fetch(fileUrl as string);

      if (!response.ok) {
        throw new Error('File fetch failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      const fileName = fileUrl.split('/').pop();

      link.download = (fileName as string) || 'downloaded-thumbnail';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // id 기준 내림차순 정렬 (최근에 추가한 것, id가 큰 것이 위로)
  const sortedThumbnails = [...thumbnails].sort((a, b) => {
    // id가 없는 경우 맨 아래로
    if (!a.id && !b.id) return 0;
    if (!a.id) return 1;
    if (!b.id) return -1;
    // id가 있는 경우 내림차순 정렬
    return (b.id || 0) - (a.id || 0);
  });

  return (
    <List
      itemLayout="vertical"
      dataSource={sortedThumbnails}
      renderItem={(thumbnail: ThumbnailInterface) => {
        const key = `${thumbnail.language}-${thumbnail.orientation}`;
        const bgColor = comboColorMap[key] || 'transparent';

        return (
          <Card className="upload-video-list-item" hoverable style={{ backgroundColor: bgColor }}>
            <div className="video-meta">
              <Form.Item>
                <Checkbox
                  checked={selectedThumbnail?.id === thumbnail.id}
                  onChange={e => {
                    if (e.target.checked) {
                      onThumbnailSelect(thumbnail);
                    } else {
                      onThumbnailSelect(null);
                    }
                  }}
                />
              </Form.Item>
              <Avatar
                shape="square"
                style={{ width: '100px', height: '100px', paddingLeft: '10px' }}
                src={thumbnail.file ? thumbnail.file : ''}
              />
              <div className="video-info">
                <Form.Item label="Name">
                  {canDownload ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Tooltip title={thumbnail.file?.split('/').pop()}>
                        <span
                          className="thumbnail-file-name"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            maxWidth: '300px',
                            verticalAlign: 'middle',
                          }}
                        >
                          {thumbnail.file?.split('/').pop()}
                        </span>
                      </Tooltip>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => handleThumbnailDownload(thumbnail)}
                        style={{ padding: '0px' }}
                      >
                        Download
                      </Button>
                    </div>
                  ) : (
                    <Input type="text" value={thumbnail.file || ''} />
                  )}
                  <Form.Item label="Language">
                    <Select
                      value={normalizeLanguage(thumbnail.language)}
                      onChange={(value: string) =>
                        isNew
                          ? onNewThumbnailChange(thumbnail, 'language', value)
                          : onThumbnailChange(thumbnail, 'language', value)
                      }
                      style={{ width: 170 }}
                      options={POSTER_LANGUAGE_OPTIONS}
                    ></Select>
                  </Form.Item>

                  <Form.Item label="Orientation">
                    <Select
                      value={thumbnail.orientation}
                      onChange={(value: string) =>
                        isNew
                          ? onNewThumbnailChange(thumbnail, 'orientation', value)
                          : onThumbnailChange(thumbnail, 'orientation', value)
                      }
                      style={{ width: 120 }}
                      options={ORIENTATION_OPTIONS}
                    ></Select>
                  </Form.Item>
                </Form.Item>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onThumbnailRemove(thumbnail)}
                >
                  delete
                </Button>
              </div>
            </div>
          </Card>
        );
      }}
    />
  );
};

export default ThumbnailList;
